import {
  Input,
  Button,
  useToast,
  Box,
  Text,
  Grid,
  PinInputField,
  PinInput,
  HStack,
  Icon,
  Spinner,
  Avatar,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { FaGoogle, FaFacebookF } from "react-icons/fa";
import { motion } from "framer-motion";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebase-config";
import { saveUserProfile, signInWithSocial, socialErrorMessage } from "../../lib/socialAuth";

const SOCIAL_BUTTONS = [
  { id: "google", label: "Continue with Google", icon: FaGoogle, bg: "white", color: "gray.700", border: "gray.200" },
  { id: "facebook", label: "Continue with Facebook", icon: FaFacebookF, bg: "#1877F2", color: "white", border: "#1877F2" },
];

export const AccountArea = ({ isOpen, onSignupComplete }) => {
  const recaptchaContainerIdRef = useRef(
    `recaptcha-container-${Math.random().toString(36).slice(2)}`
  );
  const recaptchaVerifierRef = useRef(null);
  const isMountedRef = useRef(false);
  const [step, setStep] = useState("phone"); // phone, loading, otp, name
  const [phoneNumber, setPhoneNumber] = useState("+1");
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [cameFromSocial, setCameFromSocial] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const toast = useToast();

  const recaptchaContainerId = recaptchaContainerIdRef.current;

  // Cleanup recaptcha without assuming the widget DOM still exists. Firebase's
  // reCAPTCHA script can throw if `clear()` runs after React unmounts the node.
  const cleanupRecaptcha = () => {
    const verifier = recaptchaVerifierRef.current;
    recaptchaVerifierRef.current = null;
    if (window.recaptchaVerifier === verifier) {
      window.recaptchaVerifier = null;
    }

    if (!verifier) return;

    try {
      if (document.getElementById(recaptchaContainerId)) {
        verifier.clear();
      }
    } catch (e) {
      console.warn("Ignoring reCAPTCHA cleanup error:", e);
    }
  };

  // Setup recaptcha when modal opens
  useEffect(() => {
    isMountedRef.current = true;
    if (isOpen) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setupRecaptcha();
      }, 200);
      return () => {
        clearTimeout(timer);
        cleanupRecaptcha();
      };
    }
    return () => {
      isMountedRef.current = false;
      cleanupRecaptcha();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // A closed window should reopen on the phone step, not a half-finished
  // Google or Facebook form from the last attempt.
  useEffect(() => {
    if (isOpen) return;
    setStep("phone");
    setOtp("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setAvatarUrl(null);
    setCameFromSocial(false);
    setSocialLoading(null);
  }, [isOpen]);

  const setupRecaptcha = () => {
    if (recaptchaVerifierRef.current) return recaptchaVerifierRef.current;
    
    // Wait for DOM element to exist
    const container = document.getElementById(recaptchaContainerId);
    if (!container) {
      console.log("Recaptcha container not found, will retry...");
      return null;
    }
    
    try {
      const verifier = new RecaptchaVerifier(
        auth,
        recaptchaContainerId,
        { size: "invisible" }
      );
      recaptchaVerifierRef.current = verifier;
      window.recaptchaVerifier = verifier;
      return verifier;
    } catch (e) {
      console.error("Error creating recaptcha:", e);
      return null;
    }
  };

  // Format phone for display
  const formatPhoneNumber = (value) => {
    if (!value) return "+1";
    const cleaned = value.replace(/[^\d+]/g, "");
    if (!cleaned.startsWith("+1")) return "+1";
    const digits = cleaned.slice(2);
    if (digits.length === 0) return "+1";
    if (digits.length <= 3) return `+1 (${digits}`;
    if (digits.length <= 6) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Get raw phone number for API
  const getRawPhone = (formatted) => {
    return formatted.replace(/[^\d+]/g, "");
  };

  const handlePhoneChange = (e) => {
    setPhoneNumber(formatPhoneNumber(e.target.value));
  };

  // Main flow: Send OTP (we check if user is new after verification)
  const handleContinue = async () => {
    const raw = getRawPhone(phoneNumber);
    
    if (raw.length < 12) { // +1 + 10 digits
      toast({
        title: "Enter your phone number",
        description: "Please enter a complete 10-digit phone number.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    // Ensure recaptcha is set up
    if (!recaptchaVerifierRef.current) {
      setupRecaptcha();
      // Give it a moment to initialize
      await new Promise(r => setTimeout(r, 500));
    }

    if (!recaptchaVerifierRef.current) {
      toast({
        title: "Error",
        description: "Please refresh the page and try again.",
        status: "error",
        duration: 5000,
      });
      return;
    }

    setLoading(true);
    setStep("loading");

    try {
      // Send OTP directly - we'll check if user is new after verification
      const appVerifier = recaptchaVerifierRef.current;
      const confirmation = await signInWithPhoneNumber(auth, raw, appVerifier);
      setVerificationId(confirmation.verificationId);
      
      setStep("otp");
      toast({
        title: "Code Sent!",
        description: "Check your phone for the verification code.",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error:", error);
      setStep("phone");
      
      // Handle specific errors
      let errorTitle = "Error";
      let errorDescription = "Failed to send code. Please try again.";
      
      if (error.code === "auth/too-many-requests") {
        errorTitle = "Too Many Attempts";
        errorDescription = "Please wait a few minutes before trying again.";
      } else if (error.code === "auth/invalid-phone-number") {
        errorTitle = "Invalid Phone Number";
        errorDescription = "Please check your phone number and try again.";
      } else if (error.message?.includes("reCAPTCHA")) {
        errorTitle = "Verification Error";
        errorDescription = "Please refresh the page and try again.";
      }
      
      toast({
        title: errorTitle,
        description: errorDescription,
        status: "error",
        duration: 5000,
      });
      
      // Reset recaptcha on error
      cleanupRecaptcha();
      // Delay before setting up again
      setTimeout(() => {
        if (isMountedRef.current) setupRecaptcha();
      }, 500);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (otp.length < 6) {
      toast({
        title: "Enter Code",
        description: "Please enter the 6-digit code.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);
      const userCredential = await signInWithCredential(auth, credential);
      const user = userCredential.user;

      // Check if user exists in Firestore (not just Auth)
      const userDoc = await getDoc(doc(db, "users", user.uid));
      
      if (!userDoc.exists() || !userDoc.data().displayName) {
        // New user OR incomplete profile - collect name
        setCameFromSocial(false);
        setStep("name");
        setLoading(false);
      } else {
        // Existing user with complete profile - done!
        toast({
          title: "Welcome back!",
          status: "success",
          duration: 3000,
        });
        if (onSignupComplete) onSignupComplete();
      }
    } catch (error) {
      console.error("OTP error:", error);
      toast({
        title: "Invalid Code",
        description: "Please check the code and try again.",
        status: "error",
        duration: 5000,
      });
      setLoading(false);
    }
  };

  // Complete signup with name and email
  const handleCompleteSignup = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: "Enter your name",
        description: "Please enter your first and last name.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!email.trim() || !email.includes("@")) {
      toast({
        title: "Enter your email",
        description: "Please enter a valid email address.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      if (user) {
        await saveUserProfile(user, {
          firstName,
          lastName,
          email,
          avatarUrl,
          requireAuthEmail: !cameFromSocial,
        });

        toast({
          title: "Welcome to Proximy!",
          description: "Your account has been created.",
          status: "success",
          duration: 3000,
        });

        // Notify parent that signup is complete
        if (onSignupComplete) {
          onSignupComplete();
        }
      }
    } catch (error) {
      console.error("Signup error:", error);
      
      // Handle specific error for email already in use
      let errorMessage = "Failed to complete signup. Please try again.";
      if (error.code === "auth/email-already-in-use") {
        errorMessage = "This email is already associated with another account.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const finishIfReady = (isNew) => {
    toast({
      title: isNew ? "Welcome to Proximy!" : "Welcome back!",
      status: "success",
      duration: 3000,
    });
    if (onSignupComplete) onSignupComplete();
  };

  const handleSocial = async (kind) => {
    setSocialLoading(kind);
    try {
      const outcome = await signInWithSocial(kind);
      if (outcome.complete) {
        finishIfReady(outcome.isNew);
        return;
      }
      setCameFromSocial(true);
      setFirstName(outcome.firstName || "");
      setLastName(outcome.lastName || "");
      setEmail(outcome.email || "");
      setAvatarUrl(outcome.avatarUrl || null);
      setStep("name");
    } catch (error) {
      if (
        error.code === "auth/popup-closed-by-user" ||
        error.code === "auth/cancelled-popup-request"
      ) {
        return;
      }
      console.error("Social sign-in error:", error);
      toast({
        title: "Sign-in failed",
        description: socialErrorMessage(error),
        status: "error",
        duration: 5000,
      });
    } finally {
      setSocialLoading(null);
    }
  };

  const goBack = async () => {
    if (step === "otp") {
      setStep("phone");
      setOtp("");
      return;
    }
    if (step === "name" && cameFromSocial) {
      // They're already signed in as the social account. Drop that session
      // so backing out lets them pick a different method.
      setCameFromSocial(false);
      setAvatarUrl(null);
      setFirstName("");
      setLastName("");
      setEmail("");
      setStep("phone");
      try {
        await signOut(auth);
      } catch (error) {
        console.warn("Sign out after cancelling social signup:", error);
      }
      return;
    }
    if (step === "name") {
      setStep("otp");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ width: "100%" }}
    >
      <Box pb={4} px={5}>
        <div id={recaptchaContainerId}></div>

        {/* STEP 1: Phone Number */}
        {step === "phone" && (
          <>
            {SOCIAL_BUTTONS.map((provider) => (
              <Button
                key={provider.id}
                onClick={() => handleSocial(provider.id)}
                isLoading={socialLoading === provider.id}
                isDisabled={!!socialLoading}
                mb={2}
                w="full"
                size="lg"
                bg={provider.bg}
                color={provider.color}
                border="1px solid"
                borderColor={provider.border}
                fontWeight="600"
                fontSize="15px"
                leftIcon={<Icon as={provider.icon} boxSize="16px" />}
                _hover={{ opacity: 0.92 }}
              >
                {provider.label}
              </Button>
            ))}

            <HStack my={4} spacing={3}>
              <Box flex="1" h="1px" bg="gray.200" />
              <Text fontSize="xs" color="gray.400" fontWeight="600">
                or
              </Text>
              <Box flex="1" h="1px" bg="gray.200" />
            </HStack>

            <Text fontWeight="600" fontSize="14px" color="gray.600" pb={2}>
              Enter your phone number
            </Text>
            <Input
              bg="#F2F2F2"
              size="lg"
              placeholder="+1 (234) 567-8999"
              value={phoneNumber}
              onChange={handlePhoneChange}
              fontSize="18px"
            />
            <Button
              onClick={handleContinue}
              isLoading={loading}
              mt={4}
              w="full"
              bg="#508cf7"
              size="lg"
              color="white"
              _hover={{ bg: "#226cf1" }}
            >
              Continue
            </Button>
            <Text fontSize="xs" color="gray.500" mt={3} textAlign="center">
              We'll send you a verification code
            </Text>
          </>
        )}

        {/* LOADING */}
        {step === "loading" && (
          <Box textAlign="center" py={8}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text mt={4} color="gray.600">Sending verification code...</Text>
          </Box>
        )}

        {/* STEP 2: OTP Verification */}
        {step === "otp" && (
          <>
            <Text fontWeight="600" fontSize="14px" color="gray.600" pb={2}>
              Enter the 6-digit code sent to {phoneNumber}
            </Text>
            <HStack w="100%" justifyContent="center" mb={4}>
              <PinInput 
                size="lg" 
                value={otp} 
                onChange={setOtp}
                otp
              >
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
              </PinInput>
            </HStack>
            <Grid templateColumns="48px 1fr" gap={2}>
              <Button
                onClick={goBack}
                bg="gray.200"
                size="lg"
                _hover={{ bg: "gray.300" }}
              >
                <Icon as={IoArrowBack} />
              </Button>
              <Button
                onClick={handleVerifyOtp}
                isLoading={loading}
                bg="#508cf7"
                size="lg"
                color="white"
                _hover={{ bg: "#226cf1" }}
              >
                Verify
              </Button>
            </Grid>
          </>
        )}

        {/* STEP 3: Name & Email (new users only) */}
        {step === "name" && (
          <>
            <Text fontWeight="600" fontSize="14px" color="gray.600" pb={2}>
              {cameFromSocial
                ? "Just a couple of details to finish your account"
                : "Almost done! Tell us about yourself"}
            </Text>
            {avatarUrl && (
              <HStack spacing={3} mb={4}>
                <Avatar
                  size="md"
                  name={`${firstName} ${lastName}`.trim()}
                  src={avatarUrl}
                />
                <Text fontSize="xs" color="gray.500">
                  We'll use this photo on your profile. You can change it later.
                </Text>
              </HStack>
            )}
            <Input
              bg="#F2F2F2"
              size="lg"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              mb={3}
            />
            <Input
              bg="#F2F2F2"
              size="lg"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              mb={3}
            />
            <Input
              bg="#F2F2F2"
              size="lg"
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              mb={1}
            />
            <Text fontSize="xs" color="gray.500" mb={3}>
              We'll send your ticket confirmations here
            </Text>
            <Grid templateColumns="48px 1fr" gap={2}>
              <Button
                onClick={goBack}
                bg="gray.200"
                size="lg"
                _hover={{ bg: "gray.300" }}
              >
                <Icon as={IoArrowBack} />
              </Button>
              <Button
                onClick={handleCompleteSignup}
                isLoading={loading}
                bg="#508cf7"
                size="lg"
                color="white"
                _hover={{ bg: "#226cf1" }}
              >
                Complete Signup
              </Button>
            </Grid>
          </>
        )}
      </Box>
    </motion.div>
  );
};
